import * as images from "./images";

const NAME = "ACADIE";

export const info = {
  id: "acadie",
  name: NAME,
  secondName: "AUTOMATIC CHORD MACHINE",
  image: images.overview,
  manualFile: `/resources/manuals/DawnedInstruments-${NAME}_manual.pdf`,
  extraFeatures: [
    {
      type: "settings-editor",
      routeSlug: "settings-editor",
      navLabel: "Settings\nEditor",
      usbDeviceName: NAME,
      safariDisabled: true,
      labels: {
        sectionUsbMidi: "USB MIDI",
        sectionMidi1: "MIDI 1",
        sectionMidi2: "MIDI 2",
        sectionSync: "Sync",
        sectionKeyboard: "Keyboard",
        sectionRecorder: "Recorder",
        outputChannels: "Output channels",
        labelKeyboard: "Keyboard",
        labelBass: "Bass",
        labelTouchbar: "Touchbar",
        labelFormat: "Format",
        labelOctaveShiftMode: "Octave shift mode",
        labelQuantize: "Quantize",
        labelStartMode: "Start mode",
      },
    },
    {
      type: "firmware-updates",
      routeSlug: "updates",
      navLabel: "Firmware\nUpdates",
      firmwareVersion: "1.0",
      firmwareDate: "10/02/2025",
      firmwareFile: `/resources/updates/DawnedInstruments-${NAME}_firmware_v1.0.uf2`,
      productName: NAME,
      settingsEditorSlug: "settings-editor",
      updateSteps: [
        `Turn on ${NAME} and connect it to your computer via USB.`,
        `Press <strong>REST</strong> and <strong>TIE</strong> buttons for 2 seconds until ${NAME} turns off.`,
        `Check your computer: a new USB drive named <strong>${NAME}</strong> should appear.`,
        "Drag and drop the downloaded firmware file onto the new USB drive, and wait a few seconds.",
        `When ${NAME} restarts, the update is complete.`,
      ],
    },
  ],
};

export const overview = [
  {
    image: images.overview,
    introDescription: `<strong>${NAME}</strong> is an advanced MIDI controller and analog polyphonic synthesizer that allows you to generate chords, arpeggios, and sequences.`,
    sectionsIntro: "It consists of two sections:",
    subsections: [
      {
        title: "KEYBOARD",
        text: "Enables the creation of simple or complex chords by pressing the chord buttons and their variations.",
      },
      {
        title: "TOUCHBAR",
        text: "Allows you to play, using touch sensors, the notes of the selected chord across different octaves, creating new voices in parallel with those generated by the KEYBOARD section.",
      },
    ],
    conclusion:
      "Both sections have their own MIDI output, their own analog sound engine, their own independent arpeggiator, and various options for altering the voices. A sequencer is also included, allowing you to record and playback chord sequences.",
  },
  {
    image: images.section05,
    heading: "UNLIMITED CHORDS",
    paragraphs: [
      `In addition to Major and Minor chords, ${NAME} enables the creation of countless chord variations. If the available preset variations are not enough, you can define your own custom chords (from 1 to 4 notes) and assign them to the three X1 X2 and X3 buttons.\nTo use a chord variation, first press the variation you want to use, then select the root of the chord using the buttons on the left.`,
    ],
  },
  {
    image: images.desktop06,
    heading: "UNLIMITED CONNECTIVITY",
    paragraphs: [
      `${NAME} can be powered via the USB-C connector on the rear of the unit, or with a 9V DC adapter, but it is also compatible with Eurorack, it can therefore be removed from its original wooden case and mounted and powered on Eurorack systems.`,
      "In addition to USB MIDI (class compliant, no drivers needed), it features two independent MIDI OUT ports via 3.5mm jacks TYPE-A, a CV/GATE (1V/oct) interface for the touchbar, and SYNC IN and OUT for the sequencer and the two arpeggiators.",
      "Firmware can be easily updated using USB (no additional software or drivers required).",
    ],
  },
  {
    image: images.overview01,
    heading: "ANALOG HEART",
    paragraphs: [
      `${NAME} is not just a MIDI controller; it is also a bi-timbral polyphonic analog synthesizer. Both the KEYBOARD voice and the TOUCHBAR voice are generated internally and processed through analog filters that faithfully replicate all the characteristics of the classic OM27 instrument to which ${NAME} is inspired.`,
      "The headphone output is stereo, and it is possible to route the two voices to separate output channels or mix them in mono.",
    ],
  },
  {
    image: images.overview02,
    heading: "HANDCRAFTED IN ITALY",
    paragraphs: [`Each individual ${NAME} is entirely handcrafted and carefully tested in our workshop in Italy.`],
  },
];

export const sections = [
  {
    title: "KEYBOARD",
    image: images.section01,
    description:
      "This section allows you to control the settings of the KEYBOARD voice.\nIt allows for complete configuration of the chord voicing: with BASS you can add a note one or two octaves below the root note. You can also mute the entire chord and play only the bass notes. With DUAL, you can add another set of notes to the main chord, either one octave above, one octave below, or both. With INVERT, you can achieve the first or second inversion of the chord. You can transpose the chord up or down several octaves using the OCT buttons, and with HOLD, you can keep the chord active even after releasing the chord button on the keyboard.",
  },
  {
    title: "KEYBOARD SEQUENCER & ARPEGGIATOR",
    image: images.section03,
    description: `The SEQUENCER lets you record and play back a sequence of chords. It has two modes: a "Recorder" mode where chords are recorded and played back exactly as played, and a "Step" mode where you can carefully craft you sequence step by step inserting chords, rests and ties. Adjusting the master TEMPO control on the top of ${NAME}, using the TAP button, or syncing to an external clock, allows you to change the sequence's tempo.`,
    descriptionExtra:
      "The ARPEGGIATOR can be used to arpeggiate the active chord, and can also work in conjunction with the SEQUENCER. The tempo of the arpeggio can be set independently from the Master Tempo control, or synced to it. The ARPEGGIATOR operates in two styles: a single cycle mode, and a free running mode. You can also set different arpeggio directions (up, down, up-down, down-up, and two random algorithms), and with OCT, you can select the range of octaves being arpeggiated.",
  },
  {
    title: "TOUCHBAR",
    image: images.section04,
    description: `Once a chord is active, every note (at different octaves) of the chord is available on each of the 12 touch sensors on the right side of ${NAME}, from the lowest pitch note on the bottom, to the highest pitch note on the top. \nIt is possible to customize the behavior of the touchbar: MONO ensures that only one note at a time is generated from the touchbar,  HOLD keeps the generated notes active until they are touched again, and with STRINGS it is possible to emulates common string instruments (Ukulele, Guitar, and 12-String Guitar), activating only certain touch sensors and arranging the notes as they are arranged on those instruments.`,
  },
  {
    title: "OM27 SOUND GENERATOR & TOUCHBAR ARPEGGIATOR",
    image: images.section02,
    description: `${NAME} features an analog sound engine that faithfully replicate all the characteristics of the classic OM27 instrument to which ${NAME} is inspired.\nThese controls allow you to adjust the volume of the two voices, and with SUSTAIN, you can adjust the sustain duration of the touchbar notes.  \nThe headphone jack output is STEREO, and it is possible to route the two voices to separate output channels, or mix them in mono.`,
    descriptionExtra:
      "The TOUCHBAR voice has its own independent ARPEGGIATOR, that works similarly to the one in the KEYBOARD section, with some additions: GATED mode arpeggiates only when the touchbar is touched (the sequence starts from the touched note), while FREE lets the arpeggio run continuously. In addition to the arpeggio directions available in the KEYBOARD ARPEGGIATOR, here you can setup two fully customizable arpeggio sequences, A and B",
  },
];

export const photos = {
  desktop: [images.desktop01, images.desktop02, images.desktop03, images.desktop04, images.desktop05],
  mobile: [
    images.mobile07,
    images.mobile08,
    images.mobile01,
    images.mobile02,
    images.mobile03,
    images.mobile04,
    images.mobile05,
    images.mobile06,
  ],
};

export const videos = [
  {
    url: "https://www.youtube.com/watch?v=qrwIMWfyVbE",
    isYouTube: true,
  },
  {
    url: "https://www.youtube.com/watch?v=PNZlBd-utCc",
    isYouTube: true,
  },
];

export const features = [
  "Automatic chord generation with more than 132 chords (major, minor, 7th, min7, maj7, dim, aug, add9maj, add9min, sus2, sus4) and three custom/editable chord shapes (up to 4 notes each) that can be played with every root note",
  "MIDI output through USB C (class compliant, no drivers needed) or a pair of independent MIDI TRS Type A outputs",
  "Polyphonic touch Bar with 12 gold plated touch sensors",
  'Stereo line level/headphone audio output with internal sound engine recreating the classic "OM27" sounds, using analog filters. User can select between full stereo output (Keyboard on L channel and Touchbar on R channel) or 50% mixed stereo output',
  "Three-part synthesizer: Keyboard (the selected chord), Bass (the root of the chord, lowered by octaves) and Touchbar (the individual notes of the chord played by the touch sensors)",
  'Individual controls for the Keyboard and Touchbar volume levels, Touchbar "sustain", Bass and Keyboard octaves',
  "Chord inversion (first and second inversion)",
  '"Dual" option that doubles the notes up or down (or both) an octave (only using MIDI)',
  "12 led display to visualise active notes playing",
  'Both the Keyboard and the Touchbar can be kept in "HOLD" for infinite sustain',
  'Keyboard sequencer to record and play sequences of chords, with two modes: "live" mode and "step" mode',
  "Two onboard arpeggiators and one sequencer, with a master Tempo control and Tap Tempo, externally syncable (with 3.5mm jack SYNC-IN or via MIDI CLOCK)",
  "Keyboard arpeggiator with multiple modes and directions, syncable to the master tempo or free running",
  "Touchbar arpeggiator with multiple modes and directions, and user-definable and storable sequences, syncable to the master tempo or free running",
  'Keyboard sequencer, to record and play chord sequences, with two modes ("live" and "step")',
  "Strings emulation for the Touchbar, when engaged, the notes are arranged like on strings instruments (4, 6 or 12 strings)",
  "CV/Gate outputs (1V/oct) for the Touchbar, to control external analog synthesizers.",
  "Wooden enclosure",
  "Can be removed from the enclosure and mounted/powered on a Eurorack system (panel is 42HP wide).",
  "USB C used for power, MIDI and firmware update (no drivers needed), but also +9VDC input available",
];

export const specifications = [
  "Dimensions: 8.8 x 5.5 x 1.6 inches / 224mm x 140mm x 40mm (42HP Eurorack panel)",
  "Weight: 1.1 lbs / 460gr",
  "Power Requirement: USB C, or +9VDC on 2.1mm Tip Positive Connector, or Eurorack 10pin power connector",
  "class compliant USB MIDI",
  "2x MIDI TRS Type A outputs (3.5mm jack)",
  "CV/Gate interface (0V to +5V)",
  '3.5mm (1/8") Stereo Jack -10dBV line level output (headphone compatible)',
];
